import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import { useUser } from './WebSocketContext';

const PluginImportButton = ({
  buttonProps = {},
  disabled = false,
  onImportComplete,
  openSnackbar,
  showIcon = false
}) => {
  const { t } = useTranslation();
  const { isAdmin, isEdit, permissions } = useUser();
  const [importing, setImporting] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const importInputRef = useRef(null);
  const canImportPlugin = isAdmin || isEdit || permissions === 'admin' || permissions === 'editor';

  const handleOpenImport = useCallback(() => {
    setWarningOpen(true);
  }, []);

  const handleConfirmImport = useCallback(() => {
    setWarningOpen(false);
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  }, []);

  const handleCancelImport = useCallback(() => {
    setWarningOpen(false);
  }, []);

  const notify = useCallback((message, duration = 3500, showFallbackAlert = false) => {
    if (typeof openSnackbar === 'function') {
      openSnackbar(message, duration);
      return;
    }
    if (!showFallbackAlert) {
      console.info(message);
      return;
    }
    window.alert(message);
  }, [openSnackbar]);

  const handleImportFileChange = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const data = await apiRequest('/api/v1/settings/plugins/import', {
        method: 'POST',
        body: formData
      });
      notify(t('global_setting.plugins_import_success', 'Plugin imported successfully'), 2500);
      if (typeof onImportComplete === 'function') {
        await onImportComplete(data);
      }
    } catch (error) {
      notify(
        error?.message || t('global_setting.plugins_import_error', 'Error importing plugin'),
        3500,
        true
      );
    } finally {
      setImporting(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  }, [notify, onImportComplete, t]);

  if (!canImportPlugin) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={showIcon ? <FileUploadIcon /> : undefined}
        onClick={handleOpenImport}
        disabled={disabled || importing}
        {...buttonProps}
      >
        {importing
          ? t('global_setting.plugins_importing', 'Importing...')
          : t('global_setting.plugins_import', 'Import ZIP')}
      </Button>

      <input
        ref={importInputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />

      <Dialog
        open={warningOpen}
        onClose={handleCancelImport}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('global_setting.plugins_import_warning_title', 'Import plugin ZIP?')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="warning">
              {t(
                'global_setting.plugins_import_warning_message',
                'Only import ZIP files from a trusted source. A plugin can contain executable Python code, and importing it may affect the server and project data.'
              )}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {t(
                'global_setting.plugins_import_warning_admin',
                'This action is already restricted to admin/editor accounts, but you should still inspect the archive before proceeding.'
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImport}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmImport}>
            {t('global_setting.plugins_import_warning_continue', 'Continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PluginImportButton;
